<script setup lang="ts">
import { computed } from 'vue';
import {
  UiText,
  UiCard,
  UiIcon,
  UiTooltip,
  UiLink,
  UiAvatar,
  UiChip,
  HiredOnShopexpertsBadge,
  ensureProtocol,
  generateInitials,
} from '@shopexperts/shared';
import type { ReviewImportPreviewRow } from '@shopexperts/legacy-api';

const props = defineProps<{
  row: ReviewImportPreviewRow;
}>();

const SHOPIFY_MERCHANT = 'shopify merchant';

const clientName = computed(() => {
  const parts = props.row.client.name.trim().split(/\s+/);
  if (parts.length > 1) {
    const last = parts[parts.length - 1];
    return `${parts[0]} ${last?.charAt(0)}.`;
  }
  return props.row.client.name;
});

const showCompanyLink = computed(() => {
  const url = props.row.client.company_website;
  const label = props.row.client.company_name;
  return Boolean(
    url && label && label.toLowerCase() !== SHOPIFY_MERCHANT,
  );
});

const companyUrl = computed(() => {
  return ensureProtocol(props.row.client.company_website) ?? '#';
});

const postedAt = computed(() => {
  const date = new Date(props.row.created_at);
  if (Number.isNaN(date.getTime())) return props.row.created_at;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
});

const importedFrom = computed<string | null>(() => {
  const meta = props.row.metadata as Record<string, unknown> | undefined;
  const candidate =
    (meta?.source_platform as unknown) ??
    ((meta?.normalized as Record<string, unknown> | undefined)?.source_platform as unknown);
  if (typeof candidate === 'string' && candidate.trim().length > 0) {
    return candidate.trim();
  }
  return null;
});

const sourceTooltip = computed(() => {
  if (importedFrom.value) {
    return `This review will be imported from ${importedFrom.value} and held for moderation.`;
  }
  return 'This review was previewed from the import file.';
});

const overallRating = computed(() => {
  const value = Number(props.row.overall_rating);
  return Number.isFinite(value) ? value : 0;
});

const ratingBreakdown = computed(() => [
  { label: 'Quality of Work', short: 'Quality', value: props.row.quality },
  { label: 'Communication', short: 'Communication', value: props.row.communication },
  { label: 'Turnaround Timelines', short: 'Timelines', value: props.row.timelines },
  { label: 'Value for Money', short: 'Value', value: props.row.value_for_money },
]);

const projectValueRange = computed(() => props.row.project_value_range ?? 'Unspecified');

const projectName = computed(() => {
  const direct = props.row.project_name;
  if (direct && direct.trim().length > 0) return direct.trim();
  const fromObject = props.row.project?.name;
  if (typeof fromObject === 'string' && fromObject.trim().length > 0) {
    return fromObject.trim();
  }
  return null;
});
</script>

<template>
  <UiCard
    :hover="false"
    padding="md"
    radius="sm"
  >
    <template #content>
      <div class="flex flex-col gap-4 md:gap-6">
        <!-- Header: Client profile (left) + Posted date (right) -->
        <div class="flex flex-row justify-between items-start gap-3">
          <div class="flex items-start gap-3 min-w-0">
            <UiAvatar
              :url="row.client.avatar_url || undefined"
              :initials="generateInitials(row.client.name)"
              size="xs"
            />
            <div class="flex flex-col min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <UiText as="span" variant="p" class="font-semibold">
                  {{ clientName }}
                </UiText>
                <div class="flex items-center gap-1">
                  <div
                    v-if="row.client.is_repeat_client"
                    class="border border-se-dark rounded-full flex items-center justify-center gap-1 px-1.5 py-0 h-4.5 md:h-5.5 min-w-7.5"
                  >
                    <UiIcon name="repeat" class="w-3 h-3" />
                    <UiText
                      as="span"
                      variant="caption-normal"
                      class="font-semibold whitespace-nowrap hidden md:block"
                    >
                      Repeat Client
                    </UiText>
                  </div>
                  <HiredOnShopexpertsBadge
                    :hired-on-shopexperts="row.is_hired_on_shopexperts ?? false"
                  />
                </div>
              </div>

              <UiLink
                v-if="showCompanyLink"
                variant="h6"
                :href="companyUrl"
                :label="row.client.company_name"
                external="with-arrow"
                class="text-se-highlight"
              />
              <UiText
                v-else-if="row.client.company_name"
                as="span"
                variant="h6"
              >
                {{ row.client.company_name }}
              </UiText>
            </div>
          </div>

          <UiText
            as="span"
            variant="h6"
            class="text-se-dark/65 shrink-0 whitespace-nowrap"
          >
            Posted: {{ postedAt }}
          </UiText>
        </div>

        <!-- Customer Rating + Source row -->
        <div class="flex flex-col gap-2">
          <div class="flex flex-col md:flex-row gap-2 md:gap-4">
            <div class="flex flex-wrap items-center gap-2">
              <div class="flex items-center gap-1">
                <UiText as="span" variant="h6">Customer Rating:</UiText>
                <div class="flex items-center gap-1">
                  <UiIcon name="star" class="w-4 h-4" />
                  <UiText as="span" variant="h6" class="font-semibold tabular-nums">
                    {{ overallRating.toFixed(2) }}
                  </UiText>
                </div>
              </div>

              <div class="bg-se-gray-300 rounded-full w-1.5 h-1.5 shrink-0" />

              <div class="flex items-center gap-1">
                <UiText as="span" variant="h6">Source:</UiText>
                <UiTooltip :content="sourceTooltip">
                  <div class="flex items-center gap-0.5">
                    <UiText
                      v-if="importedFrom"
                      as="span"
                      variant="h6"
                      class="font-semibold"
                    >
                      Imported from {{ importedFrom }}
                    </UiText>
                    <UiText
                      v-else
                      as="span"
                      variant="h6"
                      class="capitalize font-semibold"
                    >
                      {{ row.review_source }}
                    </UiText>
                    <UiIcon name="info-circle" class="w-3.5 h-3.5" />
                  </div>
                </UiTooltip>
              </div>
            </div>
          </div>

          <!-- Inline breakdown numbers under the main rating -->
          <div
            class="flex flex-wrap items-center gap-x-4 gap-y-1 text-se-dark/65"
          >
            <template v-for="(item, idx) in ratingBreakdown" :key="item.label">
              <span class="inline-flex items-baseline gap-1.5">
                <UiText
                  as="span"
                  variant="caption-normal"
                  class="text-se-dark/60"
                >
                  {{ item.short }}
                </UiText>
                <span class="inline-flex items-center gap-0.5">
                  <UiIcon name="star" class="w-3 h-3 text-se-dark/70" />
                  <UiText
                    as="span"
                    variant="caption-normal"
                    class="font-semibold tabular-nums text-se-dark"
                  >
                    {{ item.value.toFixed(1) }}
                  </UiText>
                </span>
              </span>
              <span
                v-if="idx < ratingBreakdown.length - 1"
                class="bg-se-gray-300 rounded-full w-1 h-1 shrink-0"
                aria-hidden="true"
              />
            </template>
          </div>

          <!-- Review body -->
          <UiText as="p" class="mt-2 wrap-break-word">{{ row.review_text }}</UiText>
        </div>

        <!-- Bottom metadata: Project Value + Project name -->
        <div
          class="flex flex-col md:flex-row gap-4 md:gap-8 items-start md:items-stretch"
        >
          <div class="flex flex-col gap-1 shrink-0">
            <UiText as="span" variant="h6">Project Value</UiText>
            <div class="flex items-center h-8">
              <UiText as="span" variant="h6" class="font-semibold">
                {{ projectValueRange }}
              </UiText>
            </div>
          </div>

          <div
            v-if="projectName"
            class="flex flex-col gap-1 min-w-0 max-w-full"
          >
            <UiText as="span" variant="h6">Project</UiText>
            <div class="flex items-center h-8 min-w-0">
              <UiText
                as="span"
                variant="h6"
                class="font-semibold truncate"
                :title="projectName"
              >
                {{ projectName }}
              </UiText>
            </div>
          </div>

          <div v-if="row.is_anonymous" class="flex flex-col gap-1 shrink-0">
            <UiText as="span" variant="h6">Visibility</UiText>
            <div class="flex items-center h-8">
              <UiChip text="Anonymous" size="xs" variant="neutral" rounded="full" />
            </div>
          </div>
        </div>
      </div>
    </template>
  </UiCard>
</template>
